"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import styles from "./CommandPalette.module.css";
import { commandPaletteIndex, type SearchItem } from "@/lib/navigation";
import { useInternalVolumeAccess } from "@/lib/useInternalVolumeAccess";

type SymbolItem = {
  ticker: string;
  name?: string;
  sector?: string;
};

let symbolCache: SymbolItem[] | null = null;
let symbolFetchPromise: Promise<SymbolItem[]> | null = null;

async function loadSymbols(): Promise<SymbolItem[]> {
  if (symbolCache) return symbolCache;
  if (symbolFetchPromise) return symbolFetchPromise;
  symbolFetchPromise = fetch("/api/symbols/list")
    .then((r) => (r.ok ? r.json() : {}))
    .then((data: unknown) => {
      // Backend returns { ok, count, symbols: [...] } — extract the array
      const list: Array<{ symbol?: string; ticker?: string; name?: string; sector?: string }> =
        Array.isArray(data) ? data : (data as { symbols?: [] }).symbols ?? [];
      symbolCache = list.map((s) => ({
        ticker: s.ticker ?? s.symbol ?? "",
        name: s.name,
        sector: s.sector,
      })).filter((s) => s.ticker);
      return symbolCache;
    })
    .catch(() => {
      symbolCache = [];
      symbolFetchPromise = null;
      return symbolCache;
    });
  return symbolFetchPromise;
}

function searchSymbols(query: string, symbols: SymbolItem[]): SymbolItem[] {
  const q = query.trim();
  if (!q) return [];
  const upper = q.toUpperCase();
  const lower = q.toLowerCase();
  return symbols
    .filter(
      (s) =>
        s.ticker.startsWith(upper) ||
        s.ticker.includes(upper) ||
        s.name?.toLowerCase().includes(lower)
    )
    .slice(0, 8);
}

const RECENTS_KEY = "cc_recent_pages";
const MAX_RECENTS = 5;

function getRecents(): SearchItem[] {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function addRecent(item: SearchItem): void {
  const prev = getRecents().filter((r) => r.href !== item.href);
  localStorage.setItem(
    RECENTS_KEY,
    JSON.stringify([item, ...prev].slice(0, MAX_RECENTS))
  );
}

function filterInternalItems(items: SearchItem[], canSeeInternalVolume: boolean): SearchItem[] {
  if (canSeeInternalVolume) return items;
  return items.filter(
    (item) => item.category !== "Scanner · Volume" && item.category !== "Internal"
  );
}

function searchIndex(query: string, canSeeInternalVolume: boolean): SearchItem[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  return filterInternalItems(commandPaletteIndex, canSeeInternalVolume).filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q) ||
      item.category?.toLowerCase().includes(q)
  );
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [recents, setRecents] = useState<SearchItem[]>([]);
  const [symbols, setSymbols] = useState<SymbolItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { allowed: canSeeInternalVolume } = useInternalVolumeAccess();

  const pageResults = searchIndex(query, canSeeInternalVolume);
  const stockResults = searchSymbols(query, symbols);

  const isTickerQuery = /^[A-Z]{1,6}$/.test(query.trim());

  const stockItems: SearchItem[] = stockResults.map((s) => ({
    label: s.ticker,
    href: `/stock/${s.ticker}`,
    description: [s.name, s.sector].filter(Boolean).join(" · "),
    category: "Stock",
  }));

  const hasQuery = query.trim().length > 0;
  const displayItems: SearchItem[] = hasQuery
    ? isTickerQuery
      ? [...stockItems, ...pageResults].slice(0, 12)
      : [...pageResults, ...stockItems].slice(0, 12)
    : recents;
  const showingRecents = !hasQuery && recents.length > 0;
  const showStocksSection = hasQuery && stockItems.length > 0;
  const showPagesSection = hasQuery && pageResults.length > 0;

  const flatIndexMap = new Map(displayItems.map((item, i) => [item, i]));

  const navigate = useCallback(
    (item: SearchItem) => {
      addRecent(item);
      setOpen(false);
      router.push(item.href);
    },
    [router]
  );

  useEffect(() => {
    const handler = () => setOpen(true);
    document.addEventListener("openCommandPalette", handler);
    return () => document.removeEventListener("openCommandPalette", handler);
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      setRecents(filterInternalItems(getRecents(), canSeeInternalVolume));
      setQuery("");
      setActiveIndex(0);
      inputRef.current?.focus();
      loadSymbols().then(setSymbols);
    }, 40);
    return () => window.clearTimeout(timer);
  }, [canSeeInternalVolume, open]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, displayItems.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = displayItems[activeIndex];
        if (item) navigate(item);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, displayItems, activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onMouseDown={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.inputRow}>
          <span className={styles.inputIcon} aria-hidden="true">⌕</span>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            placeholder="Search pages, tools, symbols…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            autoComplete="off"
            spellCheck={false}
          />
          <span className={styles.kbdHint}>ESC to close</span>
        </div>

        <div className={styles.results} role="listbox">
          {!hasQuery && !showingRecents ? (
            <div className={styles.emptyState}>Type to search pages and symbols</div>
          ) : hasQuery && stockItems.length === 0 && pageResults.length === 0 ? (
            <div className={styles.emptyState}>
              No results for &ldquo;{query}&rdquo;
            </div>
          ) : (
            <>
              {showingRecents && (
                <div className={styles.sectionLabel}>Recent</div>
              )}

              {showStocksSection && (
                <div className={styles.sectionLabel}>Stocks</div>
              )}

              {showStocksSection && stockItems.map((item) => {
                const flatIdx = flatIndexMap.get(item) ?? -1;
                return (
                  <div
                    key={`stock-${item.href}`}
                    className={`${styles.resultItem}${flatIdx === activeIndex ? ` ${styles.resultItemActive}` : ""}`}
                    role="option"
                    aria-selected={flatIdx === activeIndex}
                    onMouseEnter={() => setActiveIndex(flatIdx)}
                    onClick={() => navigate(item)}
                  >
                    <span className={styles.stockTicker}>{item.label}</span>
                    {item.description && (
                      <span className={styles.resultDesc}>{item.description}</span>
                    )}
                  </div>
                );
              })}

              {showStocksSection && showPagesSection && (
                <div className={styles.sectionDivider} />
              )}

              {showPagesSection && (
                <div className={styles.sectionLabel}>Pages</div>
              )}

              {(showPagesSection ? pageResults : showingRecents ? recents : []).map((item, i) => {
                const flatIdx = flatIndexMap.get(item) ?? -1;
                return (
                  <div
                    key={`page-${item.href}-${i}`}
                    className={`${styles.resultItem}${flatIdx === activeIndex ? ` ${styles.resultItemActive}` : ""}`}
                    role="option"
                    aria-selected={flatIdx === activeIndex}
                    onMouseEnter={() => setActiveIndex(flatIdx)}
                    onClick={() => navigate(item)}
                  >
                    <span className={styles.resultLabel}>{item.label}</span>
                    {item.description && (
                      <span className={styles.resultDesc}>{item.description}</span>
                    )}
                    {item.category && (
                      <span className={styles.resultCategory}>{item.category}</span>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className={styles.footer} aria-hidden="true">
          <span className={styles.footerKbd}>
            <kbd>↑</kbd><kbd>↓</kbd> navigate
          </span>
          <span className={styles.footerKbd}>
            <kbd>↵</kbd> open
          </span>
          <span className={styles.footerKbd}>
            <kbd>ESC</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
